import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components/macro";
import { useTranslation } from "react-i18next";
import { useParams, useHistory } from "react-router-dom";
import useSWR from "swr";
import {
  PageHeader,
  PageTitle,
  PageWrapper,
} from "../../../../../layout/portalPageLayout";
import { Breadcrumb } from "../../../../../components/ProductsLayout/SharedProductsLayout";
import { Table } from "../../../../../components/Table/Table";
import { endpoints } from "../../../../../endpoints";
import { useStoreState, TablePlaceholder } from "../../../../../util/util";
import type {
  ReferenceEntityDetailResponse,
  ReferenceEntityRow,
  ReferenceEntityCellValue,
} from "../../../../../types/types.PIM";
import { useDebounce } from "../../../../../util/hooks";
import { ErrorPlaceholder } from "../../../../../components/Error";
import { useRoutePath } from "../../../../../util/Routing";
import {
  CaretLeft,
  DownloadIcon,
  LoadingIcon,
  PlusIcon,
} from "../../../../../components/Icons/Icons";
import {
  ButtonWithConfirmDialog,
  DeleteButton,
  LinkButtonLargeV2,
  PrimaryButtonMediumV2,
  TertiaryButtonMediumV2,
} from "../../../../../components/Buttons/Buttons";
import { ColumnDef, Getter } from "@tanstack/react-table";
import axios from "axios";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { TablePageLayout } from "../../../../../components/TablePage/TablePageLayout";
import { ScrollTableWrapper } from "../../../../SharedPages/ProductDetailPage/Assets/DocumentsOrDigitalMediaTable/DocumentsOrDigitalMediaTable";
import { AddReferenceModal } from "./AddReferenceModal";
import { useAuthContext } from "../../../../../components/Auth";

const ContentWrapper = styled.div`
  padding: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
  background: none;
  border: none;
  color: ${({ theme }) => theme.primaryTextColor};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  padding: ${({ theme }) => theme.resolve("spacing_sm")} 0;
  margin-bottom: ${({ theme }) => theme.resolve("spacing_md")};

  &:hover {
    color: ${({ theme }) => theme.tertiaryTextColor};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const EntityDescription = styled.p`
  color: ${({ theme }) => theme.secondaryTextColor};
  font-size: ${({ theme }) => theme.fontSizes.small};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_lg")};
`;

type TableRow = {
  id: string;
  [key: string]: string;
};

type EntityDetail = {
  display_name: string;
  description: string;
};

export function ReferenceEntityDetail(): JSX.Element {
  const { t } = useTranslation();
  const { reference_entity_id } = useParams<{ reference_entity_id: string }>();
  const { storefront_id } = useStoreState();
  const { adminPath } = useRoutePath();
  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddReferenceModal, setShowAddReferenceModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  const [offset, setOffset] = useState(0);
  const perPage = 50;
  const [entityDetail, setEntityDetail] = useState<EntityDetail | null>(null);
  const { notifySuccess, notifyError } = useContext(Notifications);
  const { user } = useAuthContext();
  const { data, mutate, error, isValidating } =
    useSWR<ReferenceEntityDetailResponse>(
      [
        endpoints.v2_storefronts_id_reference_entities_id_data(
          storefront_id,
          reference_entity_id
        ),
        useMemo(
          () => ({
            params: {
              search: debouncedSearchQuery || undefined,
              limit: perPage,
              offset,
              order_by: "asc",
            },
          }),
          [debouncedSearchQuery, offset]
        ),
      ],
      {
        revalidateOnFocus: false,
      }
    );

  const prevEntityIdRef = useRef(reference_entity_id);
  useEffect(() => {
    if (prevEntityIdRef.current !== reference_entity_id) {
      prevEntityIdRef.current = reference_entity_id;
      setEntityDetail(null);
    }
    if (data?.entity && data.entity.id === reference_entity_id) {
      setEntityDetail((prev) => {
        if (prev?.display_name === data.entity.display_name) return prev;
        return {
          display_name: data.entity.display_name,
          description: data.entity.description,
        };
      });
    }
  }, [reference_entity_id, data?.entity]);

  // Build dynamic columns based on API response
  const columns: ColumnDef<TableRow>[] = useMemo(() => {
    if (!data?.columns) return [];

    const handleRemoveReference = async (referenceId: string) => {
      try {
        await axios.delete(
          `${endpoints.v2_storefronts_id_reference_entities_id_data(
            storefront_id,
            reference_entity_id
          )}/${referenceId}`
        );
        notifySuccess(t("Item removed successfully"));
        mutate();
      } catch (error) {
        notifyError(t("Failed to remove item"));
      }
    };

    return [
      ...data.columns
        .sort((a, b) => a.position - b.position)
        .map((column) => ({
          header: column.display_name,
          // Use accessorFn instead of accessorKey to handle attribute names with dots (e.g., "Pack. Unit")
          accessorFn: (row: TableRow) => row[column.attribute_name],
          id: column.id,
        })),
      ...(user?.rbac_role === "agilis_admin"
        ? [
            {
              header: () => <></>,
              accessorKey: "id" as string,
              cell: ({ getValue }: { getValue: Getter<string> }) => (
                <ButtonWithConfirmDialog
                  Button={(props) => <DeleteButton {...props} />}
                  testid={"remove-row-from-collection"}
                  type="button"
                  handleConfirm={() =>
                    handleRemoveReference(getValue() as string)
                  }
                  disabled={isValidating}
                  confirmMessage={t(
                    "Are you sure you want to remove this item?"
                  )}
                  //title={t("Remove Item")}
                  confirmBtnLabel={t("Remove")}
                  confirmBtnType="destructive"
                  useV2Buttons={true}
                />
              ),
            },
          ]
        : []),
    ];
  }, [
    data?.columns,
    isValidating,
    t,
    notifySuccess,
    notifyError,
    mutate,
    storefront_id,
    reference_entity_id,
    user?.rbac_role,
  ]);

  // Transform API data to table format
  const tableData: TableRow[] = useMemo(() => {
    if (!data?.data || !data?.columns) return [];

    return data.data.map((row: ReferenceEntityRow) => {
      const transformedRow: TableRow = {
        id: row.id,
      };

      // For each column, extract the value_display from the cell
      data.columns.forEach((column) => {
        const cellValue = row[column.attribute_name];
        if (cellValue && typeof cellValue === "object") {
          const cell = cellValue as ReferenceEntityCellValue;
          transformedRow[column.attribute_name] = cell.value_display;
        } else {
          transformedRow[column.attribute_name] = "";
        }
      });

      return transformedRow;
    });
  }, [data]);

  const pagination = useMemo(() => {
    if (!data?.pagination) {
      return {
        perPage,
        pageCount: 0,
        pageIndex: 0,
      };
    }

    const { total, limit } = data.pagination;
    return {
      perPage: limit,
      pageCount: Math.ceil(total / limit),
      pageIndex: Math.floor(offset / limit) + 1,
    };
  }, [data?.pagination, offset]);

  const handlePageClick = (newOffset: number) => {
    setOffset(newOffset);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setOffset(0);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setOffset(0);
  };

  const handleBack = () => {
    history.push(`${adminPath}/pim/reference-entities`);
  };

  const handleExport = () => {
    setIsExporting(true);
    axios
      .get(
        endpoints.v2_storefronts_id_reference_entities_id_data_export(
          storefront_id,
          reference_entity_id
        )
      )
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = response.headers["content-disposition"]
          .split("filename=")[1]
          .replace(/["']/g, "");
        a.click();
      })
      .catch((error) => {
        notifyError(t("Failed to export reference entity"));
      })
      .finally(() => {
        setIsExporting(false);
      });
  };

  const isLoading = isValidating && !data;

  if (error && !data) {
    return (
      <PageWrapper>
        <PageHeader>
          <PageTitle>{t("Reference Entity")}</PageTitle>
        </PageHeader>
        <ContentWrapper>
          <ErrorPlaceholder
            message={t("There was an error loading the reference entity.")}
          />
        </ContentWrapper>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Breadcrumb>
        <BackButton
          onClick={handleBack}
          style={{ marginBottom: "0", padding: "0" }}
        >
          <LinkButtonLargeV2 style={{ marginBottom: "0", padding: "0" }}>
            <CaretLeft fill="currentColor" />
            {t("Reference Entities")}
          </LinkButtonLargeV2>
        </BackButton>
      </Breadcrumb>
      <PageHeader>
        <PageTitle style={{ margin: "0" }}>
          {entityDetail?.display_name || t("Reference Entity")}
        </PageTitle>
      </PageHeader>
      <ContentWrapper style={{ padding: "0" }}>
        {entityDetail?.description && (
          <EntityDescription style={{ marginTop: "0" }}>
            {entityDetail.description}
          </EntityDescription>
        )}
        <>
          <TablePageLayout
            error={error}
            searchQuery={searchQuery}
            searchPlaceholder={t("Search...")}
            onSearchChange={handleSearch}
            onSearchClear={handleClearSearch}
            toolbarActions={
              user?.rbac_role === "agilis_admin" && (
                <>
                  <TertiaryButtonMediumV2
                    onClick={handleExport}
                    disabled={isExporting}
                    // loading={isExporting}
                  >
                    {isExporting ? (
                      <LoadingIcon width={14} height={14} />
                    ) : (
                      <DownloadIcon width={14} height={14} />
                    )}
                    {isExporting ? t("Exporting...") : t("Export")}
                  </TertiaryButtonMediumV2>
                  <PrimaryButtonMediumV2
                    onClick={() => setShowAddReferenceModal(true)}
                  >
                    <PlusIcon width={18} height={18} />
                    {t("Add Item")}
                  </PrimaryButtonMediumV2>
                </>
              )
            }
            tableSlot={
              <ScrollTableWrapper
                maxHeight={
                  pagination.pageCount > 1
                    ? "calc(100vh - 365px)"
                    : "calc(100vh - 350px)"
                }
              >
                <Table
                  columns={columns}
                  data={tableData}
                  isLoading={isLoading}
                  error={error}
                  tableHeight="100%"
                  Placeholder={
                    <TablePlaceholder message={t("No data found")} />
                  }
                />
              </ScrollTableWrapper>
            }
            pagination={pagination}
            offset={offset}
            onPageChange={handlePageClick}
          />
        </>
      </ContentWrapper>
      {showAddReferenceModal && (
        <AddReferenceModal
          show={showAddReferenceModal}
          closeModal={() => setShowAddReferenceModal(false)}
          referenceEntityId={reference_entity_id}
          title={entityDetail?.display_name ?? t("Add Reference")}
          columns={data?.columns ?? []}
          onSuccess={() => mutate()}
        />
      )}
    </PageWrapper>
  );
}
